import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FileText, RefreshCw, Search as SearchIcon } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { Box, TextField, Tooltip, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Syncfusion QueryBuilder
import { QueryBuilderComponent } from '@syncfusion/ej2-react-querybuilder';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';

// Syncfusion CSS (ensure these are imported once in your app)
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-popups/styles/material.css';
import '@syncfusion/ej2-splitbuttons/styles/material.css';
import '@syncfusion/ej2-inputs/styles/material.css';
import '@syncfusion/ej2-lists/styles/material.css';
import '@syncfusion/ej2-dropdowns/styles/material.css';
import '@syncfusion/ej2-calendars/styles/material.css';
import '@syncfusion/ej2-navigations/styles/material.css';
import '@syncfusion/ej2-querybuilder/styles/material.css';

import '../../../css/Updatedstyles.css';
import '../../../Final_Css/positiveflowbuttons.css';

// Debounce utility
const debounce = (func, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

export default function OverviewTab({ onItemClick, mode = 'recent' }) {
  const navigate = useNavigate();
  const userRole = sessionStorage.getItem("user_role"); // '8' = Manager, '9' = HR

  const [tests, setTests] = useState([]);
  const [exams, setExams] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Filter states – only mode and QueryBuilder rule remain
  const [showFilters, setShowFilters] = useState(false);
  const [filterRule, setFilterRule] = useState(null); // applied rule

  // Refs for uncontrolled QueryBuilder
  const qbRef = useRef(null);
  const currentRuleRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');


  const rowsPerPage = 10;

  // ================= BACK NAVIGATION =================
  const handleGoBack = () => {
    if (userRole === '8') navigate('/managers/dashboard');
    else if (userRole === '9') navigate('/hr/dashboard');
    else navigate('/users/dashboard');
  };

  // ================= HELPERS =================
  const badgeClass = (status) =>
    status === 'Completed' ? 'pd-badge-success'
      : status === 'Upcoming' ? 'pd-badge-warning'
        : 'pd-badge-info';

  const getStatusText = (status) => {
    if (status === 'Completed') return 'COMPLETED';
    if (status === 'Upcoming') return 'UPCOMING';
    return status || 'UNKNOWN';
  };

  // Critical Fix: Map mode correctly to backend status values
  const getApiStatus = () => {
    switch (mode) {
      case 'upcoming': return 'upcoming';
      case 'completed': return 'recent';
      default: return 'recent';
    }
  };

  const getDisplayTitle = () => {
    switch (mode) {
      case 'upcoming': return 'Upcoming Assessments';
      case 'completed': return 'Completed Assessments';
      default: return 'Tests Overview';
    }
  };

  const debouncedFetch = useRef(
    debounce((rule, search, page) => {
      fetchData(rule, search, page);
    }, 500)
  ).current;


  // ================= FETCH DATA =================
  const fetchData = useCallback(
    async (rule = null, search = '', page = currentPage) => {
      if (!userRole) {
        console.warn('userRole not ready yet');
        setLoading(false);
        return;
      }

      if (isRefreshing) return;

      const isManager = userRole === '8' || userRole === '9';
      setIsRefreshing(true);
      setLoading(true);

      try {
        const apiStatus = getApiStatus();
        const endpoint = '/managerdashboard/exam-overview/';

        const params = {
          status: apiStatus,
          page: page,
          page_size: rowsPerPage,
        };
        if (search.trim()) params.search = search;
        if (rule && rule.rules?.length) params.filters = JSON.stringify(rule);

        console.log('API Request →', endpoint, params);

        const testsRes = await axios.get(endpoint, { params });
        const data = testsRes.data || {};
        const fetchedTests = Array.isArray(data.data) ? data.data : [];

        setTests(fetchedTests);

        if (data.pagination) {
          setTotalItems(data.pagination.total || 0);
          setTotalPages(data.pagination.total_pages || 1);
        } else {
          setTotalItems(fetchedTests.length);
          setTotalPages(Math.ceil(fetchedTests.length / rowsPerPage));
        }

        // Fetch dropdowns (optional – could be removed if not used)
        const [examRes, deptRes] = await Promise.all([
          axios.get(isManager ? '/managerdashboard/exam-titles/' : '/hrdashboard/exam-titles/').catch(() => ({ data: { exams: [] } })),
          axios.get(isManager ? '/managerdashboard/all-departments/' : '/hrdashboard/all-departments/').catch(() => ({ data: { departments: [] } })),
        ]);
        setExams(Array.isArray(examRes.data?.exams) ? examRes.data.exams : []);
        setDepartments(Array.isArray(deptRes.data?.departments) ? deptRes.data.departments : []);

      } catch (err) {
        console.error('Failed to load exams:', err);
        setTests([]);
        setTotalItems(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [userRole, isRefreshing, rowsPerPage, mode]
  );

  // Trigger fetch when page, mode, filterRule, or search changes
  useEffect(() => {
    debouncedFetch(filterRule, searchQuery, currentPage);
  }, [currentPage, filterRule, searchQuery, mode]);
  // Debounced fetch (though not heavily used now because we apply filters on button click)

  // Trigger fetch when page or mode changes (filterRule applied separately via applyFilters)
  useEffect(() => {
    fetchData(filterRule, searchQuery, currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, mode]); // filterRule is intentionally omitted because we apply via button

  // Reset to page 1 when mode changes
  useEffect(() => {
    setCurrentPage(1);
  }, [mode]);


  const stringOperators = [
    { value: 'contains', key: 'Contains' },
    { value: 'equal', key: 'Equal' },
    { value: 'notequal', key: 'Not Equal' },
    { value: 'startswith', key: 'Starts With' },
    { value: 'endswith', key: 'Ends With' },
    { value: 'isempty', key: 'Is Empty' },
    { value: 'isnotempty', key: 'Is Not Empty' }
  ];

  const numberOperators = [
    { value: 'equal', key: 'Equal' },
    { value: 'notequal', key: 'Not Equal' },
    { value: 'greaterthan', key: 'Greater Than' },
    { value: 'greaterthanorequal', key: 'Greater Than Or Equal' },
    { value: 'lessthan', key: 'Less Than' },
    { value: 'lessthanorequal', key: 'Less Than Or Equal' },
    { value: 'isempty', key: 'Is Empty' },
    { value: 'isnotempty', key: 'Is Not Empty' }
  ];

  const dateOperators = [
    { value: 'equal', key: 'Equal' },
    { value: 'notequal', key: 'Not Equal' },
    { value: 'greaterthan', key: 'After' },
    { value: 'lessthan', key: 'Before' },
    { value: 'greaterthanorequal', key: 'On or After' },
    { value: 'lessthanorequal', key: 'On or Before' },
    { value: 'between', key: 'Between' },
    { value: 'isempty', key: 'Is Empty' },
    { value: 'isnotempty', key: 'Is Not Empty' }
  ];



  const queryBuilderColumns = [
    { field: 'id', label: 'Assessment Number', type: 'string', operators: stringOperators },
    { field: 'name', label: 'Assessment Title', type: 'string', operators: stringOperators },
    { field: 'dept', label: 'Department', type: 'string', operators: stringOperators },
    // { field: 'assignedTo', label: 'Assigned To', type: 'string', operators: stringOperators },
    { field: 'participants', label: 'Participants', type: 'number', operators: numberOperators },
    { field: 'examDate', label: 'Date', type: 'date', format: 'yyyy-MM-dd', operators: dateOperators },
    // { field: 'status', label: 'Status', type: 'string', operators: stringOperators },
  ];

  const defaultRule = { condition: 'and', rules: [] };

  const applyFilters = () => {
    const rule = currentRuleRef.current || defaultRule;
    setFilterRule(rule);
    setCurrentPage(1); // reset to first page
    fetchData(rule, 1);
  };

  const clearAllFilters = () => {
    setFilterRule(null);
    setSearchQuery('');
    setCurrentPage(1);
    if (qbRef.current) {
      qbRef.current.setRules(defaultRule);
      currentRuleRef.current = defaultRule;
    }
    fetchData(null, '', 1);
  };

  // ================= HANDLERS =================
  const handleRefresh = () => {
    clearAllFilters();
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const pageNumbers = getPageNumbers();

  console.log(tests, "tests from OverviewTab");

  return (
    <div className="pd-overview-container">

      {/* HEADER */}
      <div className="pd-header">
        <div className="pd-title-section">
          <button className="pd-back-button" onClick={handleGoBack} aria-label="Go back">
            <i className="fa fa-arrow-circle-left" aria-hidden="true"></i>
          </button>
          <h1 className="pd-title">{getDisplayTitle()}</h1>
          <FileText className="pd-title-icon" />
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw size={18} className={`pd-refresh-icon ${isRefreshing ? 'pd-rotating' : ''}`} />
          </button>
        </div>
      </div>

      {/* FILTER TOGGLE AND CLEAR BUTTONS */}
      {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by Assessment Number, Assessment Title, Department, Assigned to, Participants,Date ..."
          sx={{ width: 300 }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} /> }}
        />
        <ButtonComponent
          cssClass="btn"
          onClick={() => {
            if (showFilters) {
              if (qbRef.current) {
                qbRef.current.setRules(defaultRule);
                currentRuleRef.current = defaultRule;
              }

              setFilterRule(null);
              setSearchQuery('');
              setCurrentPage(1);

              fetchData(null, '', 1);
            }

            setShowFilters(!showFilters);
          }}
        >
          {showFilters ? 'Close Filters' : 'Enable Filters'}
        </ButtonComponent>

      </Box> */}


         <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          width: '100%',
        }}
      >
        <TextField
          size="small"
          placeholder="Search by Assessment Number, Assessment Title, Department, Participants, Date..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
          }}
          sx={{
            flex: 1,
            minWidth: 350,
            '& .MuiOutlinedInput-root': {
              height: '40px',
            },
          }}
        />
 
        <ButtonComponent
          cssClass="btn"
          style={{
            minWidth: '160px',
            whiteSpace: 'nowrap',
          }}
          onClick={() => {
            if (showFilters) {
              if (qbRef.current) {
                qbRef.current.setRules(defaultRule);
                currentRuleRef.current = defaultRule;
              }
 
              setFilterRule(null);
              setSearchQuery('');
              setCurrentPage(1);
 
              fetchData(null, '', 1);
            }
 
            setShowFilters(!showFilters);
          }}
        >
          {showFilters ? 'Close Filters' : 'Enable Filters'}
        </ButtonComponent>
      </Box>
 
 

      {/* SYNCFFUSION QUERYBUILDER */}
      {showFilters && (
        <div style={{ marginBottom: '20px', padding: '10px', background: '#f5f5f5', borderRadius: '8px' }}>
          <QueryBuilderComponent
            ref={qbRef}
            dataSource={[]}
            columns={queryBuilderColumns}
            rule={defaultRule}
            ruleChange={(args) => { currentRuleRef.current = args.rule; }}
            created={() => {
              if (qbRef.current) qbRef.current.setRules(defaultRule);
            }}
            showButtons={false}
            summaryView={false}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <ButtonComponent
              cssClass="btn-negative"
              onClick={() => {
                if (qbRef.current) {
                  qbRef.current.setRules(defaultRule);
                  currentRuleRef.current = defaultRule;
                }
                setFilterRule(null);
                setCurrentPage(1);
                fetchData(null, 1);
              }}
            >
              Clear Filters
            </ButtonComponent>
            <ButtonComponent cssClass="btn" onClick={applyFilters}>
              Apply Filters
            </ButtonComponent>
          </Box>
        </div>
      )}

      {/* TABLE */}
      {/* TABLE */}
      <div className="pd-table-container">
        {loading ? (
          <div className="pd-table-loading">
            <div className="pd-table-loading-animation">
              <img className="pd-table-loading-video" src="/images/breath1.gif" alt="Loading..." />
              <p>Loading {getDisplayTitle().toLowerCase()}...</p>
            </div>
          </div>
        ) : tests.length === 0 ? (
          // Show message inside table structure
          <div className="pd-table-wrapper">
            <table className="pd-table">
              <thead>
                <tr>
                  <th>Assessment Number</th>
                  <th>Assessment Title</th>
                  <th>Department</th>
                  {/* <th>Assigned To</th> */}
                  <th className="pd-numeric">Participants</th>
                  <th className="pd-numeric">Date</th>
                  {/* <th>Status</th> */}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                    {mode === 'upcoming'
                      ? 'No upcoming assessments found'
                      : mode === 'completed'
                        ? 'No completed assessments found'
                        : 'No recent assessments found'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="pd-table-wrapper">
            <table className="pd-table">
              <thead>
                <tr>
                  <th>Assessment Number</th>
                  <th>Assessment Title</th>
                  <th>Department</th>
                  {/* <th>Assigned To</th> */}
                  <th className="pd-numeric">Participants</th>
                  <th className="pd-numeric">Date</th>
                  {/* <th>Status</th> */}
                </tr>
              </thead>
              <tbody>
                {tests.map((test, idx) => (
                  <tr
                    key={test.id || idx}
                    onClick={() => onItemClick?.(test, 'exam')}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{test.id || 'N/A'}</td>
                    <td>{test.name || 'Untitled'}</td>
                    <td>{test.dept || 'Unknown Dept'}</td>
                    {/* <td>{test.assignedTo || 'Individual Employees'}</td> */}
                    <td className="pd-numeric">{test.participants || 0}</td>
                    {/* <td className="pd-numeric">{test.examDate || 'N/A'}</td> */}
                    <td className="pd-numeric">
                      {test.examDate
                        ? new Date(test.examDate).toLocaleDateString('en-GB').replace(/\//g, '-')
                        : 'N/A'}
                    </td>
                    {/* <td>
                      <span className={`pd-badge ${badgeClass(test.status)}`}>
                        {getStatusText(test.status)}
                      </span>
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION */}
        {!loading && totalItems > 0 && (
          <div className="pd-pagination">
            <button
              className="btn-negative"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Previous
            </button>
            <div className="pd-page-numbers">
              {pageNumbers.map(num => (
                <button
                  key={num}
                  className={`pg-type-btn ${currentPage === num ? 'active' : ''}`}
                  onClick={() => handlePageChange(num)}
                >
                  {num}
                </button>
              ))}
            </div>
            <button
              className="btn-negative"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}